
import { Accordion, AccordionItem, AccordionPanel, AccordionButton, AccordionIcon, Box, Button, FormControl, FormLabel, FormHelperText, Input, ModalBody, ModalFooter, Text, UnorderedList, ListItem } from "@chakra-ui/react";
import { useEffect, useState } from "react";
import { CollecticonPlusSmall } from "@devseed-ui/collecticons-chakra";

import DropZone from "./DropZone";
import FileList from "./FileList";
import { Scene } from "../../../../types/uploads";
import { MAX_FILE_SIZE, ACCEPTED_TYPES, MIN_DATE, MAX_DATE } from "../../../../settings";
import API from "../../../../api";
import { formatAsDateTimeLocalValue, zonedTimeToUtc } from "../../../../utils/timezone";

function validateFile(file?: File): string {
  if (!file) return "";

  if (!ACCEPTED_TYPES.includes(file.type)) {
    return "The uploaded file is not accepted. Select a TIFF.";
  }

  if (file.size > MAX_FILE_SIZE) {
    return "The file size exceeds the limit of 20GB. Upload a smaller file.";
  }

  return "";
}

function getDateFromFileName(fileName: string) {
  const months = ["JAN","FEB","MAR","APR","MAY","JUN","JUL","AUG","SEP","OCT","NOV","DEC"];
  const dateRegex = new RegExp(`^(\\d{2})(${months.join("|")})(0[1-9]|[12][0-9]|3[01])([01][1-9]|2[1-3])([0-5][0-9])([0-5][0-9])`);
  const match = fileName.match(dateRegex);

  if (match) {
    const [y, _, d, hh, mm, ss] = match.slice(1, 7).map((n) => parseInt(n, 10));  // eslint-disable-line @typescript-eslint/no-unused-vars
    const utcDate = new Date(Date.UTC(y + 2000, months.indexOf(match[2]), d, hh, mm, ss));
    return utcDate.toISOString();
  }

  return "";
}

type Props = {
  files?: Scene[];
  onSelect: (files: Scene) => void;
  onNext: () => void;
  onCancel: () => void;
}

function SelectFiles({ files, onSelect, onNext, onCancel }: Props) {
  const [ panchromatic, setPanchromatic ] = useState<File>();
  const [ multiSpectral, setMultiSpectral ] = useState<File>();
  const [ isSelecting, setIsSelecting ] = useState<boolean>(true);
  const [ date, setDate ] = useState<string>("");
  const [ error, setError ] = useState<string>();

  const panchromaticError = validateFile(panchromatic);
  const multiSpectralError = validateFile(multiSpectral);

  const handleUpload = () => {
    if (!(panchromatic && multiSpectral)) {
      setError("Select one panchromatic and one multispectral image.");
      return;
    }

    if (!date) {
      setError("Unable to extract date from file. Please set the date manually.");
      return;
    }

    setIsSelecting(false);
    setError("");
    const key = crypto.randomUUID();
    API.createScene(key, new Date(date).toISOString())
      .then(({ properties }) => onSelect({
        key: properties.s3_prefix,
        date: new Date(date).toISOString(),
        panchromatic,
        multiSpectral
      }))
      .catch((e) => setError(e.message));
  };

  const handleNext = () => onNext();

  useEffect(() => {
    // Try to extract the date from the file name, ensuring the dates of both files match
    if (panchromatic && multiSpectral) {
      const panchromaticFileDate = panchromatic && getDateFromFileName(panchromatic.name);
      const multiSpectralFileDate = multiSpectral && getDateFromFileName(multiSpectral.name);

      if (panchromaticFileDate && multiSpectralFileDate && panchromaticFileDate === multiSpectralFileDate) {
        setDate(panchromaticFileDate);
      }
    }
  }, [panchromatic, multiSpectral, panchromaticError, multiSpectralError]);

  useEffect(() => {
    if (isSelecting) {
      setPanchromatic(undefined);
      setMultiSpectral(undefined);
      setDate("");
    }
  }, [isSelecting]);

  const handleDateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checkValidity()) {
      // Interpret the field's timezone-less value as Pacific Time, not the browser's local timezone
      const utcDate = zonedTimeToUtc(e.target.value);
      if (!Number.isNaN(utcDate.getTime())) {
        setDate(utcDate.toISOString());
      }
    }
  };

  return (
    <>
      <ModalBody>
        <Text as="h3" fontSize="md" mb="4">Select Files</Text>
        <FileList files={files} />
        {isSelecting ? (
          <Box display="flex" flexDirection="column" gap="2" fontSize="sm">
            <Text>Each scene requires a multi-spectral and panchromatic image. Select the appropriate files below.</Text>
            <Accordion allowToggle defaultIndex={[0]}>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left" fontWeight="semibold" fontSize="sm">
                      Image Requirements
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <UnorderedList fontSize="sm">
                    <ListItem>Multispectral: 8 bands</ListItem>
                    <ListItem>Panchromatic: 1 band</ListItem>
                    <ListItem>Files should be geo-referenced</ListItem>
                    <ListItem>Files must have the same dimensions</ListItem>
                    <ListItem>Files may be 16bit or 8bit unsigned integer</ListItem>
                    <ListItem>Files should be in geographic or local projections</ListItem>
                  </UnorderedList>
                </AccordionPanel>
              </AccordionItem>
              <AccordionItem>
                <h2>
                  <AccordionButton>
                    <Box as="span" flex="1" textAlign="left" fontWeight="semibold" fontSize="sm">
                      Upload Instructions
                    </Box>
                    <AccordionIcon />
                  </AccordionButton>
                </h2>
                <AccordionPanel pb={4}>
                  <UnorderedList fontSize="sm">
                    <ListItem>While files are uploading, application browser tab must remain open</ListItem>
                    <ListItem>After images are uploaded, feel free to close this tab - processing occurs in the background</ListItem>
                  </UnorderedList>
                </AccordionPanel>
              </AccordionItem>
            </Accordion>
            <Box>
              <DropZone handleFileDrop={setMultiSpectral}>
                <Text fontWeight="semibold">Multi-spectral image</Text>
                <Text fontSize="small" opacity="0.8">File must be a TIFF with maximum size of 20GB.</Text>
              </DropZone>
              {multiSpectralError && <Text mt="2" bg="red.200" color="base.500" fontSize="small" padding={2} borderRadius={4}>{multiSpectralError}</Text>}
            </Box>

            <Box>
              <DropZone handleFileDrop={setPanchromatic}>
                <Text fontWeight="semibold">Panchromatic image</Text>
                <Text fontSize="small" opacity="0.8">File must be a TIFF with maximum size of 20GB.</Text>
              </DropZone>
              {panchromaticError && <Text mt="2" bg="red.200" color="base.500" fontSize="small" padding={2} borderRadius={4}>{panchromaticError}</Text>}
            </Box>
            { panchromatic && multiSpectral && (
              <FormControl>
                <FormLabel fontWeight="semibold">Set scene collection date (Pacific Time)</FormLabel>
                <Input
                  type="datetime-local"
                  required
                  min={formatAsDateTimeLocalValue(MIN_DATE)}
                  max={formatAsDateTimeLocalValue(MAX_DATE)}
                  value={date ? formatAsDateTimeLocalValue(new Date(date)) : ""}
                  onChange={handleDateChange}
                />
                <FormHelperText>
                  {!date
                    ? "The scene collection date cannot be inferred from the file names, or the file dates do not match. "
                    : ""}
                  Times are interpreted as Pacific Time (America/Los_Angeles), regardless of your computer&apos;s timezone.
                </FormHelperText>
              </FormControl>
            )}
          </Box>
        ) : (
          <Button
            mt="4"
            w="100%"
            variant="base-outline"
            onClick={() => setIsSelecting(true)}
            leftIcon={<CollecticonPlusSmall />}
          >
            Upload another scene
          </Button>
        )}
        {error && <Text mt="4" bg="red.200" color="base.500" fontSize="small" padding={2} borderRadius={4}><strong>Error:</strong> {error}</Text>}
      </ModalBody>
      <ModalFooter display="flex" gap="5">
        <Button variant="ghost" onClick={onCancel}>Cancel</Button>
        {isSelecting ? (
          <Button onClick={handleUpload}>Upload scene</Button>
        ) : (
          <Button onClick={handleNext}>Next: Select models</Button>
        )}
      </ModalFooter>
    </>
  );
}

export default SelectFiles;
